import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { Input, Button, Row, Col } from "antd";
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import {
  get_device_control_system,
  add_device_control_system,
} from "../../../utils/api";
import MainCard from '@/components/MainCard';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/App';
import DataTable from '@/components/DataTable';
import PageHeader, { EmptyState } from '@/components/PageHeader';

function DcsConfig({ show_table, addForm, set_get_status, get_status }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const labelColor = isDark ? '#cbd5e1' : '#374151';
  const formRef = useRef();
  const [dcsData, setdcsData] = useState([]);
  const [unit, setUnits] = useState();
  const [show_form, set_show_form] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dcsReq, setdcsReq] = useState({
    name: "",
    description: "",
    ip_address: "",
    port: "",
    protocol: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (dcsReq.name && dcsReq.ip_address && dcsReq.port && dcsReq.protocol) {
      add_dcs(dcsReq);
    } else {
      toast.warning("Please Enter All the Details");
    }
  };

  // table columns
  const dcsDetailColumns = [
    {
      dataIndex: "dcs_name",
      title: "Name",
      key: "name",
      width: "15%",
      align: "center",
    },
    {
      dataIndex: "description",
      title: "Description",
      key: "description",
      width: "25%",
      align: "center",
    },
    {
      dataIndex: "ip_address",
      title: "IP Address",
      key: "ip_address",
      width: "15%",
      align: "center",
    },
    {
      dataIndex: "port",
      title: "Port",
      key: "port",
      sortType: "number",
      width: "10%",
      align: "center",
    },
    {
      dataIndex: "protocol",
      title: "Protocol",
      key: "protocol",
      width: "10%",
      align: "center",
    },
  ];

  //####################### Handle Api's######################################################

  const get_all_dcss = async () => {
    setLoading(true);
    try {
      await get_device_control_system(success_dcs_list_get);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(error?.response?.data?.message || "Failed to load DCS configuration");
    } finally {
      setLoading(false);
    }
  };

  const success_dcs_list_get = (res) => {
    if (res?.data?.status === "success") {
      setdcsData(res?.data?.data);
    } else {
      setdcsData([]);
    }
  };

  const add_dcs = async (dcsData) => {
    try {
      await add_device_control_system(dcsData, success_add_dcs);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(error?.response?.data?.message || "Failed to save DCS");
    }
  };

  const success_add_dcs = (res) => {
    console.log(res);
    formRef.current.reset();
    if (res.status === "failed") {
      toast.warning(res.message);
    } else {
      formRef.current.reset();
      set_get_status(!get_status);
      get_all_dcss();
      setdcsReq({
        name: "",
        description: "",
        ip_address: "",
        port: "",
        protocol: "",
      });
      toast.success("dcs added Successfully");
    }
  };

  useEffect(() => {
    get_all_dcss();
  }, []);

  const formOpen = show_form || addForm;

  return (
    <>
      <MainCard
        title={
          <PageHeader
            compact
            icon={<Icons.Settings size={22} />}
            title={t('DCS Config')}
            description="Distributed control systems that the plant integrates with."
          />
        }
        secondary={
          !show_table && !addForm ? (
            <Button
              type={show_form ? 'default' : 'primary'}
              icon={show_form ? <CloseOutlined /> : <PlusOutlined />}
              onClick={() => set_show_form(!show_form)}
            >
              {show_form ? 'Close form' : 'Add DCS'}
            </Button>
          ) : null
        }
      >
        <Row gutter={[16, 16]}>
          {formOpen && (
            <Col span={24}>
              <form ref={formRef} onSubmit={handleSubmit}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <div className="flex flex-col gap-1">
                      <label htmlFor="name" style={{ color: labelColor, fontSize: 13 }}>
                        Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="name"
                        type="text"
                        name="name"
                        placeholder="Enter Name"
                        value={dcsReq["name"]}
                        required
                        onChange={(e) => setdcsReq({ ...dcsReq, name: e.target.value })}
                      />
                    </div>
                  </Col>
                  <Col xs={24} md={8}>
                    <div className="flex flex-col gap-1">
                      <label htmlFor="description" style={{ color: labelColor, fontSize: 13 }}>
                        Description
                      </label>
                      <Input
                        id="description"
                        type="text"
                        name="description"
                        placeholder="Enter Description"
                        value={dcsReq["description"]}
                        onChange={(e) => setdcsReq({ ...dcsReq, description: e.target.value })}
                      />
                    </div>
                  </Col>
                  <Col xs={24} md={8}>
                    <div className="flex flex-col gap-1">
                      <label htmlFor="ip_address" style={{ color: labelColor, fontSize: 13 }}>
                        IP Address <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="ip_address"
                        type="text"
                        name="ip_address"
                        placeholder="Enter IP Address"
                        value={dcsReq["ip_address"]}
                        required
                        onChange={(e) => setdcsReq({ ...dcsReq, ip_address: e.target.value })}
                      />
                    </div>
                  </Col>
                  <Col xs={24} md={8}>
                    <div className="flex flex-col gap-1">
                      <label htmlFor="port" style={{ color: labelColor, fontSize: 13 }}>
                        Port <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="port"
                        type="number"
                        name="port"
                        placeholder="Enter Port"
                        value={dcsReq["port"]}
                        required
                        onChange={(e) => setdcsReq({ ...dcsReq, port: e.target.value })}
                      />
                    </div>
                  </Col>
                  <Col xs={24} md={8}>
                    <div className="flex flex-col gap-1">
                      <label htmlFor="protocol" style={{ color: labelColor, fontSize: 13 }}>
                        Protocol <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="protocol"
                        type="text"
                        name="protocol"
                        placeholder="Enter Protocol"
                        value={dcsReq["protocol"]}
                        required
                        onChange={(e) => setdcsReq({ ...dcsReq, protocol: e.target.value })}
                      />
                    </div>
                  </Col>
                  <Col span={24}>
                    <div className="flex justify-end gap-2">
                      <Button size="small" htmlType="reset">
                        Reset
                      </Button>
                      <Button size="small" type="primary" htmlType="submit">
                        Submit
                      </Button>
                    </div>
                  </Col>
                </Row>
              </form>
            </Col>
          )}
          {!show_table && (
            <Col span={24}>
              <DataTable
                tableKey="dcsConfigTable"
                searchable
                searchKeys={['dcs_name', 'description', 'ip_address', 'protocol']}
                data={dcsData}
                columns={dcsDetailColumns}
                rowKey={(r) => r.id ?? r.dcs_name}
                loading={loading}
                bordered
                height={null}
                locale={{
                  emptyText: (
                    <EmptyState
                      title="No DCS configured yet"
                      description="Register a distributed control system to start streaming process data."
                      actionLabel="Add DCS"
                      onAction={() => set_show_form(true)}
                    />
                  ),
                }}
              />
            </Col>
          )}
        </Row>
      </MainCard>
    </>
  );
}

export default DcsConfig;
